import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type RefObject } from 'react';
import { useWikilinkSuggestions } from '@/hooks/useWikilinkSuggestions';
import { getCaretCoordinates } from '@/lib/textareaCaret';
import {
  applyWikilinkSuggestion,
  detectWikilinkTrigger,
  rankWikilinkSuggestions,
  type WikilinkTrigger,
} from '@/lib/wikilinkAutocomplete';

interface UseWikilinkAutocompleteOptions {
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  value: string;
  onChange: (value: string) => void;
}

interface Position {
  top: number;
  left: number;
}

/**
 * Detects an in-progress `[[wikilink` the user is typing in a `<textarea>`
 * and drives a floating suggestion dropdown: tracks the caret-relative
 * popup position, fetches matching article titles, and handles
 * arrow-key/Enter/Escape navigation without stealing focus from the
 * textarea (the textarea remains the active element throughout — this is
 * the same pattern used by @mention autocomplete in chat apps).
 */
export function useWikilinkAutocomplete({ textareaRef, value, onChange }: UseWikilinkAutocompleteOptions) {
  const [trigger, setTrigger] = useState<WikilinkTrigger | undefined>();
  const [position, setPosition] = useState<Position | undefined>();
  const [activeIndex, setActiveIndex] = useState(0);

  // Re-examines the textarea's current value + caret and updates trigger
  // state accordingly. Called after every keystroke and after caret-only
  // moves (click, arrow keys) via onSelect.
  const recompute = useCallback(() => {
    const el = textareaRef.current;
    if (!el || el.selectionStart !== el.selectionEnd) {
      setTrigger(undefined);
      setPosition(undefined);
      return;
    }

    const caret = el.selectionStart;
    const next = detectWikilinkTrigger(el.value, caret);
    setTrigger(next);

    if (next) {
      const coords = getCaretCoordinates(el, caret);
      setPosition({ top: coords.top + coords.height, left: coords.left });
      setActiveIndex(0);
    } else {
      setPosition(undefined);
    }
  }, [textareaRef]);

  // `recompute` is stable (only depends on the ref), so re-running it
  // whenever `value` changes is exactly the "re-check after every
  // keystroke" behavior we want.
  useEffect(() => {
    recompute();
  }, [value, recompute]);

  const query = trigger?.query ?? '';

  // Debounce relay queries while the user is actively typing the article
  // name — otherwise every keystroke fires a fresh `search` REQ. Reset
  // immediately (no debounce) whenever a *new* `[[` session starts, keyed
  // by its start offset, so switching to a different wikilink doesn't show
  // stale results while waiting for the debounce timer.
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  const sessionStart = trigger?.start;
  const lastSessionStart = useRef(sessionStart);
  const debounceTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!trigger) return;
    if (lastSessionStart.current !== sessionStart) {
      lastSessionStart.current = sessionStart;
      setDebouncedQuery(query);
      return;
    }
    debounceTimer.current = setTimeout(() => setDebouncedQuery(query), 200);
    return () => clearTimeout(debounceTimer.current);
  }, [query, trigger, sessionStart]);

  const { data: allSuggestions = [], isFetching } = useWikilinkSuggestions(debouncedQuery, Boolean(trigger));
  const suggestions = rankWikilinkSuggestions(allSuggestions, query);

  useEffect(() => {
    setActiveIndex((i) => Math.min(i, Math.max(0, suggestions.length - 1)));
  }, [suggestions.length]);

  const close = useCallback(() => {
    setTrigger(undefined);
    setPosition(undefined);
  }, []);

  const select = useCallback(
    (index: number) => {
      const el = textareaRef.current;
      const suggestion = suggestions[index];
      if (!el || !trigger || !suggestion) return;

      const result = applyWikilinkSuggestion(value, trigger, suggestion);
      onChange(result.value);
      close();

      // The caller's onChange triggers a re-render with the new value on
      // the next tick; restore focus/caret once that's committed.
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(result.caret, result.caret);
      });
    },
    [textareaRef, trigger, suggestions, value, onChange, close],
  );

  /** Intercepts navigation keys while the dropdown is open. Returns true if the key was consumed. */
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>): boolean => {
      if (!trigger || suggestions.length === 0) return false;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setActiveIndex((i) => (i + 1) % suggestions.length);
          return true;
        case 'ArrowUp':
          e.preventDefault();
          setActiveIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
          return true;
        case 'Enter':
        case 'Tab':
          e.preventDefault();
          select(activeIndex);
          return true;
        case 'Escape':
          e.preventDefault();
          close();
          return true;
        default:
          return false;
      }
    },
    [trigger, suggestions, activeIndex, select, close],
  );

  return {
    isOpen: Boolean(trigger) && (isFetching || suggestions.length > 0),
    position,
    suggestions,
    query,
    isLoading: isFetching,
    activeIndex,
    setActiveIndex,
    select,
    close,
    handleKeyDown,
    /** Attach to the textarea's onSelect/onClick/onKeyUp to keep the trigger in sync with caret moves that don't change `value`. */
    onSelectionChange: recompute,
  };
}
